import React from 'react';
import { Moon, Compass, Clock, Gauge, CloudOff } from 'lucide-react';

const EYEBROW = 'text-gold-foreground uppercase text-xs font-semibold tracking-[0.14em]';

const focusHeroSearch = () => {
  const input = document.querySelector<HTMLInputElement>('input[placeholder*="Moab"]');
  input?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  input?.focus();
};

export const TrackerExplainerSection: React.FC = () => {
  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-16 pb-8 sm:pt-20 sm:pb-10">
      <div className="max-w-2xl mb-10">
        <span className={`${EYEBROW} mb-3 block`}>How it works</span>
        <h2 className="text-2xl text-foreground tracking-[-0.02em]">How this Milky Way tracker works</h2>
        <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
          Without this planner, I have to check moonrise, twilight, and core position across several apps before every trip. Here's what it answers, and what it can't tell you.
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <article className="p-6 sm:p-8 rounded-xl border border-border">
          <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center mb-4">
            <Moon className="w-5 h-5 text-gold" aria-hidden="true" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-3">Is the Milky Way visible tonight?</h3>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Enter your location and the planner rates every night from 1 to 10 for that spot. The galactic core has to be above the horizon at some point during astronomical darkness, or the night scores 0, which can happen at high latitudes even in midsummer. If the core is up, the moon's brightness sets the rating, with a 10 for a nearly new moon and lower scores as more of the moon is lit.
            </p>
          </div>
        </article>
        <article className="p-6 sm:p-8 rounded-xl border border-border">
          <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center mb-4">
            <Compass className="w-5 h-5 text-gold" aria-hidden="true" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-3">Where is the galactic core, and which direction do I face?</h3>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              The core sits in Sagittarius. From the Northern Hemisphere it stays low toward the south, rising in the southeast and moving west through the night. From the Southern Hemisphere it climbs much higher and can pass nearly overhead in their winter.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              The planner gives you rise and set times with compass directions for your location, so you can line the core up with a foreground before you drive out.
            </p>
          </div>
        </article>
        <article className="p-6 sm:p-8 rounded-xl border border-border">
          <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center mb-4">
            <Clock className="w-5 h-5 text-gold" aria-hidden="true" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-3">When does the core rise and set?</h3>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              It depends on your latitude and the time of year. From Moab, at about 38°N, the core rises in the southeast a couple of hours after dark in early May, and it comes up earlier as the month goes on.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Farther north the window shrinks. Above roughly 48.5°N, summer nights never reach astronomical darkness. Iceland is the hard case: the summer sky stays bright, and at that latitude the galactic core doesn't clear the horizon.
            </p>
          </div>
        </article>
        <article className="p-6 sm:p-8 rounded-xl border border-border">
          <div className="w-11 h-11 rounded-xl bg-muted flex items-center justify-center mb-4">
            <Gauge className="w-5 h-5 text-gold" aria-hidden="true" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-3">Why a 1–10 rating instead of a visibility map?</h3>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              A light pollution map tells you where the sky is dark, and the planner has one. The rating answers a narrower question: is this night worth it at this spot? Core position, moon, and darkness can be worked out years ahead, which is why planning months out works.
            </p>
          </div>
        </article>
      </div>
      <div className="mt-4 rounded-xl p-6 sm:p-8 bg-gold/5 border border-gold/25 grid gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,0.8fr)] md:items-center">
        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
          <div className="w-11 h-11 shrink-0 rounded-xl bg-white flex items-center justify-center">
            <CloudOff className="w-5 h-5 text-gold" aria-hidden="true" />
          </div>
          <div>
            <span className={`${EYEBROW} mb-2 block`}>Not in the score</span>
            <p className="text-sm text-muted-foreground leading-relaxed text-pretty">
              Weather, haze, smoke, and terrain aren't in the score. A canyon wall can block a low core, and clouds can wipe out a 10. I check a cloud and transparency forecast about a week out, and again the day before.
            </p>
          </div>
        </div>
        <p className="text-sm text-foreground leading-relaxed border-t border-gold/25 pt-6 md:border-t-0 md:border-l md:pl-6 md:pt-0">
          Type a location into the{' '}
          <button
            type="button"
            className="inline-block py-2.5 -my-2.5 text-gold-dark underline underline-offset-2 hover:text-gold"
            onClick={focusHeroSearch}
          >
            search box above
          </button>
          {' '}to see this month's ratings. It's free, and you don't need an account.
        </p>
      </div>
    </section>
  );
};
